import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Air } from "@air/next-icons";
import { VisuallyHidden } from "@air/primitive-visually-hidden";
import { useBreakpointsContext } from "@air/provider-media-query";
import classNames from "classnames";
import isString from "lodash/isString";
import Link from "next/link";
import { useRouter } from "next/router";
import { NextSeo } from "next-seo";
import { WorkspaceAvatarAndName } from "~/components/WorkspaceAvatarAndName";
import { Routes } from "~/constants/routes";
import { AUTH_LAYOUT_HEADER } from "~/constants/testIDs";
import { useShowIntercomOnMount } from "~/hooks/useShowIntercomOnMount";
const LogoLink = (props)=>/*#__PURE__*/ _jsxs(Link, {
        ...props,
        children: [
            /*#__PURE__*/ _jsx(VisuallyHidden, {
                children: "Go back to landing page."
            }),
            /*#__PURE__*/ _jsx(Air, {
                className: "w-[76px] fill-blue-9",
                "aria-hidden": "true"
            })
        ]
    });
const formWidth = 420;
export const AuthLayout = (param)=>{
    let { children, layout = {}, banner, className } = param;
    const { isAboveSmallScreen } = useBreakpointsContext();
    const { pathname } = useRouter();
    useShowIntercomOnMount(![
        Routes.auth.logout,
        Routes.auth.oauth
    ].includes(pathname) && isAboveSmallScreen);
    const { logoHref = Routes.root, nextSeoProps, visibleTitleText, visibleSubtitle, workspaceInfo, mainClassName, visibleSubtitleClassNames, maxTextWidth = formWidth, testimonial, extraContent } = layout;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(NextSeo, {
                ...nextSeoProps
            }),
            /*#__PURE__*/ _jsx("div", {
                className: classNames("h-screen", className),
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-col md:min-h-screen md:flex-row",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex-1",
                            children: [
                                !workspaceInfo ? /*#__PURE__*/ _jsx("header", {
                                    className: "flex h-[70px] w-full items-center justify-between border-t-2 border-teal-7",
                                    children: /*#__PURE__*/ _jsx(LogoLink, {
                                        className: "ml-6 md:ml-8",
                                        href: logoHref
                                    })
                                }) : /*#__PURE__*/ _jsx("header", {
                                    className: "w-full border-b border-t-2 border-b-grey-5 border-t-teal-7",
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        className: "mx-4 grid h-[72px] grid-cols-1 md:grid-cols-3",
                                        children: [
                                            workspaceInfo !== "loading" ? /*#__PURE__*/ _jsx(WorkspaceAvatarAndName, {
                                                name: workspaceInfo.name,
                                                image: workspaceInfo.avatarImageSource,
                                                size: "large",
                                                className: "mr-0 md:mr-5"
                                            }) : // add empty box to prevent logo jumping when avatar is loading
                                            /*#__PURE__*/ _jsx("div", {}),
                                            /*#__PURE__*/ _jsx(LogoLink, {
                                                className: "justify-self-center",
                                                href: logoHref
                                            })
                                        ]
                                    })
                                }),
                                !!children && /*#__PURE__*/ _jsxs("main", {
                                    className: classNames("mt-12 px-5 pb-20", mainClassName),
                                    children: [
                                        banner,
                                        (!!visibleTitleText || !!visibleSubtitle) && /*#__PURE__*/ _jsxs("div", {
                                            className: "mx-auto text-left",
                                            style: {
                                                maxWidth: maxTextWidth
                                            },
                                            children: [
                                                !!visibleTitleText && /*#__PURE__*/ _jsx("h1", {
                                                    className: classNames("text-center font-gelica text-32 font-semibold tracking-[.03em] text-grey-12 sm:text-40 sm:tracking-[.03em]", visibleSubtitle ? "mb-1.5 md:mb-3" : "mb-8 md:mb-10"),
                                                    "data-testid": AUTH_LAYOUT_HEADER,
                                                    children: visibleTitleText
                                                }),
                                                isString(visibleSubtitle) ? /*#__PURE__*/ _jsx("p", {
                                                    className: classNames("mb-7 text-grey-11", visibleSubtitleClassNames),
                                                    children: visibleSubtitle
                                                }) : visibleSubtitle
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "mx-auto",
                                            style: {
                                                maxWidth: formWidth
                                            },
                                            children: children
                                        })
                                    ]
                                })
                            ]
                        }),
                        testimonial,
                        extraContent
                    ]
                })
            })
        ]
    });
};
